import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BillerSearch } from "@/components/BillerSearch";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, CreditCard, Banknote, Smartphone, MessageCircle,
  Loader2, Plus, ShieldCheck, AlertCircle, ExternalLink, Copy, User, Wallet,
  Zap, Flame, Droplet, Tv, Wifi, HeartPulse, Car, Trash2, RotateCcw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/hooks/useFeatures";
import { CopyableId } from "@/components/CopyableId";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = [
  { key: "credit_card",     label: "Credit card",     icon: CreditCard, idHint: "credit-card",     inputLabel: "Consumer ID",  inputHint: "As defined by biller (e.g. mobile or card number)" },
  { key: "loan_emi",        label: "Loan EMI",        icon: Banknote,   idHint: "loan-emi",        inputLabel: "Loan account #",   inputHint: "Loan account number" },
  { key: "electricity",     label: "Electricity",     icon: Zap,        idHint: "electricity",     inputLabel: "Consumer number",   inputHint: "Consumer / RR number" },
  { key: "gas",             label: "Gas",             icon: Flame,      idHint: "gas",             inputLabel: "CA / consumer #",   inputHint: "CA number / consumer number" },
  { key: "water",           label: "Water",           icon: Droplet,    idHint: "water",           inputLabel: "RR / consumer #",   inputHint: "RR / consumer number" },
  { key: "mobile_postpaid", label: "Mobile Postpaid", icon: Smartphone, idHint: "mobile-postpaid", inputLabel: "Mobile number",     inputHint: "10-digit mobile" },
  { key: "mobile_prepaid",  label: "Mobile Prepaid",  icon: Smartphone, idHint: "mobile-prepaid",  inputLabel: "Mobile number",     inputHint: "10-digit mobile" },
  { key: "dth",             label: "DTH",             icon: Tv,         idHint: "dth",             inputLabel: "Subscriber ID",     inputHint: "DTH subscriber ID" },
  { key: "broadband",       label: "Broadband",       icon: Wifi,       idHint: "broadband",       inputLabel: "Account ID",        inputHint: "Account / subscriber ID" },
  { key: "insurance",       label: "Insurance",       icon: HeartPulse, idHint: "insurance",       inputLabel: "Policy number",     inputHint: "Policy number" },
  { key: "fastag",          label: "FASTag",          icon: Car,        idHint: "fastag",          inputLabel: "Vehicle / Tag ID", inputHint: "Vehicle / FASTag ID" },
];

export default function BbpsV2Wizard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { isEnabled: featureEnabled, loading: featuresLoading } = useFeatures();
  const [search] = useSearchParams();
  const initialCat = (search.get("category") || "credit_card").replace(/-/g, "_");
  // Phase 7.37h — Default to self-mode unless URL explicitly says ?self=0
  const initialSelf = search.get("self") !== "0";

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(initialCat);
  const cat = useMemo(() => CATEGORIES.find((c) => c.key === category) || CATEGORIES[0], [category]);
  const Icon = cat.icon;

  // Self-transaction mode (Phase 7.18 — applied to BBPS v2)
  // When true: partner pays own credit-card / loan bill from their own wallet.
  // Skips Step 1 (mobile/OTP) and customer KYC; still goes through L1/L2/L3 approval.
  const [isSelf, setIsSelf] = useState(initialSelf);

  // Admin-controlled category enablement — combines TWO sources:
  //   1. `useFeatures()` — feature matrix per-role flags `bbps_<category>`
  //      (primary source: admin → Feature matrix)
  //   2. `/api/landing/services` — landing page visibility flags
  // A category is rendered as ACTIVE only if BOTH agree it's enabled. The
  // landing services lookup remains for back-compat / SEO control but the
  // feature matrix is what gates transactions per role.
  const [enabledCategorySlugs, setEnabledCategorySlugs] = useState(null);
  useEffect(() => {
    let cancel = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/landing/services`)
      .then((r) => r.ok ? r.json() : { services: null })
      .then((data) => {
        if (cancel) return;
        if (Array.isArray(data?.services)) {
          setEnabledCategorySlugs(new Set(data.services.map((s) => s.slug)));
        } else {
          // Network/parse issue → allow everything (don't lock out partners).
          setEnabledCategorySlugs(new Set(CATEGORIES.map((c) => c.key)));
        }
      })
      .catch(() => {
        if (!cancel) setEnabledCategorySlugs(new Set(CATEGORIES.map((c) => c.key)));
      });
    return () => { cancel = true; };
  }, []);

  // Customer KYC banner state (Phase 7.20 — KYC enforced at /customer/send-otp)
  const [kycBlocked, setKycBlocked] = useState(null); // {kyc_status, message}

  // Step 1 — customer mobile + OTP
  const [customerMobile, setCustomerMobile] = useState("");
  const [otpChannel, setOtpChannel] = useState("sms");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [reusedSession, setReusedSession] = useState(false);
  const [busy, setBusy] = useState(false);

  // Step 2 — pick / add consumer card
  const [cards, setCards] = useState([]);
  const [pickedCardId, setPickedCardId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newCard, setNewCard] = useState({ nickname: "", consumer_id: "", consumer_name: "", biller_id: "", biller_name: "" });
  const [newCardAdFields, setNewCardAdFields] = useState({}); // dynamic ad fields per biller
  const [billers, setBillers] = useState([]);
  const [billersLoading, setBillersLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [validatedAmount, setValidatedAmount] = useState(null);
  const [validatedDue, setValidatedDue] = useState(null);
  const [validateError, setValidateError] = useState(null);  // string | null — surfaced in dialog
  const [validateRaw, setValidateRaw] = useState(null);      // raw bill response for transparency
  const [deletingCardId, setDeletingCardId] = useState(null);

  // Step 3 — amount
  const [billAmount, setBillAmount] = useState("");
  const [billDetails, setBillDetails] = useState(null); // full bill fetch response for display
  const [cardAdOverride, setCardAdOverride] = useState({}); // ad_fields for old cards missing stored values
  const [retailerCharge, setRetailerCharge] = useState("");
  const [retailerChargeMode, setRetailerChargeMode] = useState("flat"); // "flat" | "pct"
  const [retailerChargePct, setRetailerChargePct] = useState("");
  const [retailerChargeCaps, setRetailerChargeCaps] = useState({ max_flat: 0, max_pct: 0 });
  const [refreshing, setRefreshing] = useState(false);

  // When user picks a card, auto-prefill billAmount from the saved snapshot
  useEffect(() => {
    if (!pickedCardId) return;
    const card = cards.find((c) => c.id === pickedCardId);
    if (card?.last_bill_amount && !billAmount) {
      setBillAmount(String(card.last_bill_amount));
    }
    setCardAdOverride({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedCardId]);

  const refreshBill = async () => {
    if (!pickedCardId) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/bbps/v2/consumer-cards/${pickedCardId}/fetch-bill`, {
        mobile: isSelf ? (user?.mobile || "") : customerMobile,
        ad_fields: cardAdOverride,
      });
      setBillDetails(data); // store full response
      if (data.bill_amount != null) {
        setBillAmount(String(data.bill_amount));
        toast.success(`Refreshed · ₹${data.bill_amount}${data.due_date ? ` · due ${data.due_date}` : ""}`);
        setCards((cs) => cs.map((c) => c.id === pickedCardId ? {
          ...c,
          last_bill_amount: data.bill_amount,
          last_bill_due_date: data.due_date,
          last_bill_min_amount: data.min_amount,
          last_bill_max_amount: data.max_amount,
        } : c));
      } else {
        toast.error("BBPS returned no amount");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  // Auto-fetch bill when entering Step 3 — fetchId from response is required for pay_bill API
  const goToStep3 = async () => {
    if (!pickedCardId) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/bbps/v2/consumer-cards/${pickedCardId}/fetch-bill`, {
        mobile: isSelf ? (user?.mobile || "") : customerMobile,
        ad_fields: cardAdOverride,
      });
      setBillDetails(data);
      if (data.bill_amount != null) {
        setBillAmount(String(data.bill_amount));
        setCards((cs) => cs.map((c) => c.id === pickedCardId ? {
          ...c, last_bill_amount: data.bill_amount, last_bill_due_date: data.due_date,
          last_bill_min_amount: data.min_amount, last_bill_max_amount: data.max_amount,
        } : c));
      }
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bill fetch failed — cannot proceed");
    } finally { setRefreshing(false); }
  };


  // Load retailer charge caps from admin config
  const loadRetailerCaps = async () => {
    try {
      const { data } = await api.get(`/bbps/v2/retailer-charge-limits?category=${category}`);
      setRetailerChargeCaps({ max_flat: data.max_flat || 0, max_pct: data.max_pct || 0 });
    } catch {
      // silent — use defaults (no cap)
    }
  };
  useEffect(() => { loadRetailerCaps(); /* eslint-disable-next-line */ }, [category]);

  // Step 4 — gateway + method
  const [paymentMethod, setPaymentMethod] = useState("in_shop");
  const [gateway, setGateway] = useState("customer_wallet");

  // Live fee preview from backend — keeps the Confirm total honest.
  const [feePreview, setFeePreview] = useState(null); // { platform_fee, total_payer_pays, ... }
  const [previewLoading, setPreviewLoading] = useState(false);

  // Final
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // ────────── Self-mode toggle ──────────
  // Switching ON: pre-fill partner mobile, skip Step 1, jump to Step 2 + load self-cards.
  // Switching OFF: reset everything back to a fresh customer-pays flow.
  const enterSelfMode = async () => {
    setIsSelf(true);
    const ownMobile = user?.mobile || "";
    setCustomerMobile(ownMobile);
    setOtpToken("self"); // sentinel — backend ignores otp_token in self-mode
    setOtpSent(false);
    setOtp("");
    setReusedSession(false);
    if (ownMobile) await loadCards(ownMobile);
    setStep(2);
  };

  const exitSelfMode = () => {
    setIsSelf(false);
    setCustomerMobile("");
    setOtpToken("");
    setOtpSent(false);
    setOtp("");
    setCards([]);
    setPickedCardId("");
    setStep(1);
  };

  // ────────── Step 1 ──────────
  const sendOtp = async () => {
    if (!/^[6-9][0-9]{9}$/.test(customerMobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    setBusy(true);
    setKycBlocked(null);
    try {
      const { data } = await api.post("/customer/send-otp", { customer_mobile: customerMobile, channel: otpChannel });
      if (data.reused_session && data.customer_otp_token) {
        setOtpToken(data.customer_otp_token);
        setReusedSession(true);
        await loadCards(customerMobile);
        toast.success("Customer already verified · skipping OTP");
        setStep(2);
        return;
      }
      setOtpSent(true);
      setDevOtp(data.dev_otp);
      setWhatsappUrl(data.whatsapp_url || "");
      toast.success(otpChannel === "whatsapp" ? "WhatsApp link ready" : `OTP sent${data.dev_otp ? ` (dev: ${data.dev_otp})` : ""}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Phase 7.20 — KYC required at OTP step
      if (status === 412 && detail && typeof detail === "object" && detail.code === "customer_kyc_required") {
        setKycBlocked({ kyc_status: detail.kyc_status, message: detail.message, mobile: detail.customer_mobile });
        toast.error(`Customer KYC ${detail.kyc_status === "missing" ? "missing" : detail.kyc_status} — verify before sending OTP.`);
      } else {
        toast.error(formatApiError(detail) || "Failed to send OTP");
      }
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/customer/verify-otp", { customer_mobile: customerMobile, otp });
      setOtpToken(data.customer_otp_token);
      await loadCards(customerMobile);
      toast.success("Verified");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "OTP verify failed");
    } finally { setBusy(false); }
  };

  // ────────── Step 2 ──────────
  const loadCards = async (mobile) => {
    try {
      const { data } = await api.get(`/bbps/v2/consumer-cards?customer_mobile=${mobile}`);
      const filtered = (data.items || []).filter((c) => c.category === category);
      setCards(filtered);
      if (filtered.length > 0) setPickedCardId(filtered[0].id);
    } catch {
      // silent
    }
  };

  // ────────── Customer wallet panel (balance + quick top-up) ──────────
  const [customerWallet, setCustomerWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupGateway, setTopupGateway] = useState("razorpay");
  const [topupChannel, setTopupChannel] = useState(""); // ManaRecharge channel: pay0/pay3/...
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  // Dynamic top-up options (gateways + ManaRecharge channels) for THIS partner.
  // Replaces the previous hardcoded gateway list — only enabled + fee-configured
  // PGs are presented, and the channel list is fetched live (with fee-config fallback).
  const [topupOptions, setTopupOptions] = useState({ gateways: [], channels: { manarecharge: [] } });
  const [topupOptionsLoaded, setTopupOptionsLoaded] = useState(false);
  useEffect(() => {
    if (!topupOpen) return;
    if (topupOptionsLoaded) return;
    (async () => {
      try {
        const { data } = await api.get("/wallet/topup-options");
        setTopupOptions(data || { gateways: [], channels: { manarecharge: [] } });
        // Default-select the first available gateway if the current one isn't available.
        const avail = (data?.gateways || []).filter((g) => g.available);
        if (avail.length > 0 && !avail.some((g) => g.id === topupGateway)) {
          setTopupGateway(avail[0].id);
          setTopupChannel("");
        }
      } catch {
        // Fall back to a permissive default so the dialog never blocks the user.
        setTopupOptions({
          gateways: [
            { id: "razorpay", label: "Razorpay", available: true, supports_channel: false },
            { id: "cashfree", label: "Cashfree", available: true, supports_channel: false },
            { id: "manarecharge", label: "ManaRecharge", available: true, supports_channel: true },
            { id: "runpaisa", label: "Runpaisa", available: true, supports_channel: false },
          ],
          channels: { manarecharge: [] },
        });
      } finally {
        setTopupOptionsLoaded(true);
      }
    })();
  }, [topupOpen, topupOptionsLoaded, topupGateway]);

  const availableGateways = (topupOptions.gateways || []).filter((g) => g.available);
  const manaChannels = topupOptions.channels?.manarecharge || [];
  const manaChannelsLoading = !topupOptionsLoaded && topupGateway === "manarecharge";

  // Live fee preview for the customer wallet top-up (admin-configured PG MDR + Settlement).
  // Re-fires whenever amount/gateway/channel changes — debounced 350 ms so the partner
  // sees the actual fee that will be charged BEFORE clicking submit.
  const [topupFeePreview, setTopupFeePreview] = useState(null);
  const [topupFeeError, setTopupFeeError] = useState("");
  const [topupFeeLoading, setTopupFeeLoading] = useState(false);
  useEffect(() => {
    const amt = parseFloat(topupAmount);
    if (!topupOpen || !amt || amt <= 0 || !topupGateway) {
      setTopupFeePreview(null); setTopupFeeError(""); return;
    }
    setTopupFeeLoading(true);
    setTopupFeeError("");
    let cancel = false;
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.post("/topup-fees/preview", {
          gateway: topupGateway, amount: amt, channel: topupChannel || null,
        });
        if (!cancel) setTopupFeePreview(data);
      } catch (err) {
        if (!cancel) {
          setTopupFeePreview(null);
          setTopupFeeError(formatApiError(err.response?.data?.detail) || "Fee preview unavailable");
        }
      } finally {
        if (!cancel) setTopupFeeLoading(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(tid); };
  }, [topupOpen, topupAmount, topupGateway, topupChannel]);

  const loadCustomerWallet = async (mobile) => {
    if (!mobile || isSelf) return;
    setWalletLoading(true);
    try {
      const { data } = await api.get(`/customer-wallet/balance/${mobile}`);
      setCustomerWallet(data);
    } catch {
      setCustomerWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };

  const submitCustomerTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt < 1) { toast.error("Enter a valid amount"); return; }
    setTopupSubmitting(true);
    try {
      const payload = {
        mobile: customerMobile, amount: amt, gateway: topupGateway,
      };
      if (topupGateway === "manarecharge" && topupChannel) {
        payload.pg = topupChannel;
      }
      const { data } = await api.post("/customer-wallet/topup/order", payload);
      toast.success("Top-up order created. Customer pays at PG.");
      // Open the hosted-checkout in a new tab. The redirect/webhook will mark
      // the topup as paid + credit the wallet. Different PGs use different
      // field names — try them all in priority order.
      const checkoutUrl =
        data?.sdk_params?.checkout_url ||         // ManaRecharge, Cashfree
        data?.sdk_params?.payment_link ||         // Razorpay payment-link
        data?.sdk_params?.short_url ||            // Razorpay link object
        data?.sdk_params?.redirect_url ||         // Runpaisa
        data?.sdk_params?.url || "";
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Top-up created but the PG did not return a checkout URL. Check the topup record under wallet › transactions.");
      }
      setTopupOpen(false);
      setTopupAmount("");
      // Refresh balance soon — best-effort, customer may still be paying.
      setTimeout(() => loadCustomerWallet(customerMobile), 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Top-up failed");
    } finally {
      setTopupSubmitting(false);
    }
  };

  // Refresh whenever OTP is verified or the mobile changes.
  useEffect(() => {
    if (otpToken && customerMobile && !isSelf) loadCustomerWallet(customerMobile);
    // eslint-disable-next-line
  }, [otpToken, customerMobile, isSelf]);

  useEffect(() => { if (otpToken && customerMobile) loadCards(customerMobile); /* eslint-disable-next-line */ }, [category]);

  // If URL had ?self=1, auto-enter self-mode once the user is loaded.
  useEffect(() => {
    if (initialSelf && user?.mobile && !customerMobile) {
      enterSelfMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openAddCard = async () => {
    setNewCard({ nickname: "", consumer_id: "", consumer_name: "", biller_id: "", biller_name: "" });
    setNewCardAdFields({});
    setValidated(false); setValidatedAmount(null); setValidatedDue(null);
    setValidateError(null); setValidateRaw(null);
    setBillers([]);
    setAddOpen(true);
    // Load billers from the BBPS provider (kebab-case category)
    const kebabCat = category.replace(/_/g, "-");
    setBillersLoading(true);
    try {
      const { data } = await api.get(`/bbps/billers/${kebabCat}`);
      setBillers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load billers");
    } finally {
      setBillersLoading(false);
    }
  };

  const validateConsumer = async () => {
    if (!newCard.biller_id) { toast.error("Pick a biller first"); return; }
    if (!newCard.consumer_id || newCard.consumer_id.length < 3) { toast.error("Enter a valid consumer ID"); return; }
    setValidating(true);
    setValidateError(null); setValidateRaw(null);
    try {
      const kebabCat = category.replace(/_/g, "-");
      const autoMobile = isSelf ? (user?.mobile || "") : customerMobile;
      // consumer_id goes as-is into canumber — its meaning is defined by biller's displayname
      // (e.g. "Registered Mobile Number" for credit cards, "Vehicle Registration Number" for FASTag)
      const rawId = String(newCard.consumer_id || "").replace(/\s+/g, "");
      const payload = {
        category: kebabCat,
        consumer_id: rawId,
        biller_id: newCard.biller_id,
      };
      if (autoMobile && /^[6-9]\d{9}$/.test(autoMobile)) {
        payload.mobile = autoMobile;
      }
      // Collect ad_field values entered by the user + auto-filled mobile fields
      const adValues = {};
      Object.entries(newCardAdFields || {}).forEach(([k, v]) => { if (v) adValues[k] = v; });
      // Auto-fill ad_fields that ask for "mobile"/"phone" with customer mobile
      const selBiller = billers.find((b) => b.id === newCard.biller_id);
      (selBiller?.ad_fields || []).forEach((f) => {
        if (!f?.name) return;
        const fnLower = (f.display_name || f.name || "").toLowerCase();
        if ((fnLower.includes("mobile") || fnLower.includes("phone")) && !adValues[f.key] && autoMobile) {
          adValues[f.key] = autoMobile;
        }
      });
      if (Object.keys(adValues).length > 0) payload.ad_fields = adValues;

      const { data } = await api.post("/bbps/bill-fetch", payload, { timeout: 25000 });
      const bill = data.bill || data;
      const detectedName = bill.consumer_name || bill.customer_name || bill.name || "";
      const detectedAmount = bill.bill_amount ?? bill.amount ?? null;
      const dueDate = bill.due_date || null;
      setValidateRaw(bill);
      // Also seed billDetails for Step 3 display
      setBillDetails(bill);

      setNewCard((c) => ({ ...c, consumer_name: detectedName || c.consumer_name }));
      setValidatedAmount(detectedAmount);
      setValidatedDue(dueDate);
      setValidated(true);

      if (detectedName) {
        toast.success(
          `Validated · ${detectedName}` +
          (detectedAmount != null && detectedAmount > 0 ? ` · bill ${formatINR(detectedAmount)}` : "") +
          (dueDate ? ` · due ${dueDate}` : "")
        );
      } else {
        toast.success("BBPS confirmed · enter consumer name manually below.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = formatApiError(detail) || err?.message || "BBPS validation failed";
      setValidated(false);
      setValidateError(msg);
      setValidateRaw(err?.response?.data?.raw || null);
      toast.error(msg);
    } finally { setValidating(false); }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm("Delete this saved biller? This cannot be undone.")) return;
    setDeletingCardId(cardId);
    try {
      await api.delete(`/bbps/v2/consumer-cards/${cardId}`);
      toast.success("Saved biller deleted");
      if (pickedCardId === cardId) setPickedCardId("");
      await loadCards(customerMobile);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    } finally { setDeletingCardId(null); }
  };

  const addNewCard = async () => {
    if (!validated) { toast.error("Validate via BBPS before saving"); return; }
    if (!newCard.nickname) { toast.error("Nickname required"); return; }
    if (!newCard.consumer_name || newCard.consumer_name.trim().length < 2) { toast.error("Consumer name required"); return; }
    setBusy(true);
    try {
      const biller = billers.find((b) => b.id === newCard.biller_id) || {};
      const rawId = String(newCard.consumer_id || "").replace(/\s+/g, "");
      const payload = {
        category,
        nickname: newCard.nickname,
        consumer_id: rawId,
        consumer_name: newCard.consumer_name,
        biller_id: newCard.biller_id,
        biller_name: biller.name || newCard.biller_id,
        last_bill_amount: validatedAmount,
        ad_fields: newCardAdFields || {},
      };
      if (isSelf) {
        payload.is_self = true;
      } else {
        payload.customer_mobile = customerMobile;
        payload.customer_otp_token = otpToken;
      }
      const { data } = await api.post("/bbps/v2/consumer-cards", payload);
      toast.success("Card saved");
      setAddOpen(false);
      await loadCards(customerMobile);
      setPickedCardId(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add card");
    } finally { setBusy(false); }
  };

  // ────────── Step 5 — submit ──────────
  const submit = async () => {
    const amt = parseFloat(billAmount); const chg = isSelf ? 0 : effectiveRetailerCharge;
    if (!amt || amt <= 0) { toast.error("Bill amount required"); return; }
    // Validate against min/max
    const card = cards.find((c) => c.id === pickedCardId);
    const minAmt = card?.last_bill_min_amount;
    const maxAmt = card?.last_bill_max_amount;
    if (minAmt && amt < minAmt) { toast.error(`Amount must be at least ₹${minAmt}`); return; }
    if (maxAmt && amt > maxAmt) { toast.error(`Amount cannot exceed ₹${maxAmt}`); return; }
    setSubmitting(true);
    try {
      trackEvent("bill_payment_initiated", { category: category || "unknown", amount: amt });
      const payload = {
        consumer_card_id: pickedCardId,
        bill_amount: amt,
        retailer_charge: isNaN(chg) ? 0 : chg,
        payment_method: paymentMethod,
        gateway: isSelf ? "partner_wallet" : gateway,
        // Pass bill fetch context for pay_bill API (fetchId, billNumber, aadhar)
        bill_fetch_id: billDetails?.fetch_id || "",
        bill_number: billDetails?.bill_number || "",
        bill_aadhar: billDetails?.aadhar || "",
      };
      if (isSelf) {
        payload.is_self = true;
      } else {
        payload.customer_mobile = customerMobile;
        payload.customer_otp_token = otpToken;
      }
      const { data } = await api.post("/bbps/v2/initiate", payload);
      setResult(data);
      const did = data?.transaction?.display_id;
      // Attribution: tie this transaction back to the original FB/Google ad
      // (if any) — fires only when localStorage has an attribution payload.
      try {
        const { reportLandingConversion } = await import("@/lib/landingAttribution");
        reportLandingConversion(
          data?.transaction?.id || did,
          data?.transaction?.collected_amount || data?.transaction?.amount || 0,
          // BBPS V2 categories map 1:1 to landing slugs.
          card?.category || category,
        );
      } catch { /* never block success UX on analytics */ }
      trackEvent("bill_payment_completed", {
        category: card?.category || category,
        amount: data?.transaction?.collected_amount || data?.transaction?.amount || 0,
        txn_id: data?.transaction?.display_id || "",
      });
      if (isSelf) {
        toast.success(did ? `Submitted · Txn ${did} · sent to L1 approval` : "Submitted from your wallet · sent to L1 approval");
        if (refreshUser) refreshUser();
      } else {
        const base = data.mocked ? "Mock submission · sent to L1 approval" : (paymentMethod === "link" ? "Payment link generated" : "Order created");
        toast.success(did ? `${base} · Txn ${did}` : base);
      }
      setStep(6);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not initiate");
    } finally { setSubmitting(false); }
  };

  // Compute effective retailer charge based on mode
  const effectiveRetailerCharge = (() => {
    if (isSelf) return 0;
    if (retailerChargeMode === "pct") {
      const pct = parseFloat(retailerChargePct) || 0;
      const base = parseFloat(billAmount) || 0;
      return Math.round(base * pct / 100 * 100) / 100;
    }
    return parseFloat(retailerCharge) || 0;
  })();

  // Live fee preview — fetch from backend whenever the inputs that affect the
  // total change. Keeps the "Customer pays / You'll pay" amount honest by
  // including admin platform fees (slab + pct + OTP) in addition to bill + retailer.
  useEffect(() => {
    const bill = parseFloat(billAmount) || 0;
    if (!category || bill <= 0) { setFeePreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/bbps/v2/preview-fee", {
          params: {
            category,
            amount: bill,
            payment_method: paymentMethod,
            is_self: isSelf,
            retailer_charge: effectiveRetailerCharge,
          },
        });
        if (!cancelled) setFeePreview(data);
      } catch {
        if (!cancelled) setFeePreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [category, billAmount, paymentMethod, isSelf, effectiveRetailerCharge]);

  // Backend is the source of truth — show its total when available, else
  // fall back to bill+retailer (will be off by the platform fee until preview loads).
  const platformFee = feePreview?.platform_fee || 0;
  const collectedAmount = feePreview?.total_payer_pays != null
    ? feePreview.total_payer_pays
    : (parseFloat(billAmount) || 0) + effectiveRetailerCharge;

  return (
    <DashboardLayout>
      <button onClick={() => navigate("/bbps")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3" data-testid="back-to-bbps">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to BBPS categories
      </button>
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-md grid place-items-center bg-primary/10 text-primary">
          <Icon className="h-6 w-6" strokeWidth={1.5} />
        </div>
        <div>
          <span className="overline">BBPS · Customer-pays + 3-level approval</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{cat.label} bill payment</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-xl">Customer authenticates via OTP, pays the bill via your gateway, then it flows through L1 → L2 → L3 approval before settlement.</p>
        </div>
      </div>

      {/* Mode banner — Self transaction toggle (Phase 7.18 BBPS v2) */}
      <div className="mt-5 surface-card p-4" data-testid="bbpsv2-mode-banner">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Pay for</span>
          <div className="inline-flex rounded-md border bg-background p-1" role="tablist">
            <button
              type="button"
              onClick={() => { if (isSelf) exitSelfMode(); }}
              data-testid="bbpsv2-mode-customer"
              className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${!isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
            >
              Customer
            </button>
            <button
              type="button"
              onClick={() => { if (!isSelf) enterSelfMode(); }}
              data-testid="bbpsv2-mode-self"
              className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
            >
              <User className="h-3.5 w-3.5 inline mr-1" /> Self transaction
            </button>
          </div>
          <span className="text-xs text-muted-foreground">
            {isSelf
              ? <><Wallet className="h-3 w-3 inline mr-1" /> Pay your own bill from YOUR wallet · L1/L2/L3 approval still applies</>
              : "Customer authenticates via OTP and pays via gateway"}
          </span>
        </div>
      </div>

      {/* Category quick-switch — also reflects landing-service enablement.
          Categories disabled by admin (not in /admin/landing-services) appear
          GREYED + disabled, so partners see they exist but can't transact. */}
      <div className="mt-5 flex gap-1.5 sm:gap-2 flex-wrap">
        {CATEGORIES.map((c) => {
          // Feature matrix is primary; landing services secondary.
          const featureKey = `bbps_${c.key}`;
          const matrixEnabled = featuresLoading ? true : featureEnabled(featureKey);
          const landingEnabled = enabledCategorySlugs == null ? true : enabledCategorySlugs.has(c.key);
          const isEnabled = matrixEnabled && landingEnabled;
          const reason = !matrixEnabled
            ? "Disabled in admin → Feature matrix for your role."
            : !landingEnabled
              ? "Disabled in admin → Landing services & ads."
              : "";
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => {
                if (!isEnabled) {
                  toast.error(`${c.label}: ${reason}`);
                  return;
                }
                setCategory(c.key);
                setStep(isSelf ? 2 : 1);
              }}
              disabled={!isEnabled}
              data-testid={`switch-cat-${c.key}`}
              data-disabled={!isEnabled ? "true" : undefined}
              title={isEnabled ? "" : reason}
              className={[
                "px-2.5 sm:px-3 py-1.5 rounded-full border text-[11px] sm:text-xs font-medium leading-tight",
                !isEnabled
                  ? "border-border bg-muted/40 text-muted-foreground line-through cursor-not-allowed opacity-50"
                  : category === c.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-accent",
              ].join(" ")}
            >
              <c.icon className="h-3 w-3 inline mr-1" /> {c.label}
              {!isEnabled && <span className="ml-1 text-[9px] uppercase tracking-wider">inactive</span>}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6 lg:mt-8">
        {/* Steps */}
        <div className="lg:col-span-2 surface-card p-4 sm:p-6 lg:p-8">
          {/* Step 1 */}
          {step === 1 && !isSelf && (
            <section data-testid="v2-step-1">
              <h3 className="font-display text-xl font-semibold">1 · Customer mobile</h3>
              <p className="text-sm text-muted-foreground mt-1">SMS or WhatsApp OTP. 30-min session reuse — no re-OTP within that window.</p>

              {kycBlocked && (
                <div className="mt-4 rounded-md border border-warning/40 bg-warning/5 p-4 text-sm" data-testid="v2-kyc-banner">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">Customer KYC required</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Status: <Badge variant="outline" className="ml-1 border-warning/40 text-warning">{kycBlocked.kyc_status || "missing"}</Badge>
                        <span className="ml-2">{kycBlocked.message || "Complete the customer's KYC before sending OTP."}</span>
                      </div>
                      <Button
                        size="sm"
                        className="mt-3"
                        onClick={() => navigate(`/customer-kyc?mobile=${kycBlocked.mobile}&return=/bbps/v2?category=${category}`)}
                        data-testid="v2-kyc-go"
                      >
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify KYC now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-5">
                <Label>Channel</Label>
                <div className="grid grid-cols-2 gap-2 mt-1 max-w-sm">
                  <button type="button" onClick={() => setOtpChannel("sms")} disabled={otpSent}
                    data-testid="v2-otp-channel-sms"
                    className={`rounded-md border px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-2 ${otpChannel === "sms" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <Smartphone className="h-4 w-4" /> SMS
                  </button>
                  <button type="button" onClick={() => setOtpChannel("whatsapp")} disabled={otpSent}
                    data-testid="v2-otp-channel-whatsapp"
                    className={`rounded-md border px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-2 ${otpChannel === "whatsapp" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </button>
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Mobile</Label>
                  <Input data-testid="v2-customer-mobile" inputMode="numeric" maxLength={10}
                    value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, ""))}
                    disabled={otpSent} className="mt-1 font-mono" placeholder="98xxxxxxxx" />
                </div>
                {!otpSent ? (
                  <Button onClick={sendOtp} disabled={busy || customerMobile.length !== 10} data-testid="v2-send-otp" className="self-end">
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Smartphone className="h-4 w-4 mr-1" />}
                    Send OTP
                  </Button>
                ) : (
                  <Button onClick={() => { setOtpSent(false); setOtp(""); setDevOtp(null); }} variant="outline" className="self-end">Edit number</Button>
                )}
              </div>
              {otpSent && whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs mt-3 text-success hover:underline" data-testid="v2-whatsapp-link">
                  <ExternalLink className="h-3 w-3" /> Open WhatsApp with OTP
                </a>
              )}
              {otpSent && (
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Enter OTP</Label>
                    <Input data-testid="v2-otp-input" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      className="mt-1 font-mono tracking-widest text-lg" placeholder="000000" />
                    {devOtp && <p className="text-[11px] text-warning mt-1">Dev OTP: <code className="font-mono">{devOtp}</code></p>}
                  </div>
                  <Button onClick={verifyOtp} disabled={busy || otp.length < 4} data-testid="v2-verify-otp" className="self-end">
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                    Verify & continue
                  </Button>
                </div>
              )}
            </section>
          )}

          {/* Step 2 — pick / add card */}
          {step === 2 && (
            <section data-testid="v2-step-2">
              <h3 className="font-display text-xl font-semibold">2 · Select {cat.label}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isSelf
                  ? <>Your saved {cat.label.toLowerCase()}s <Badge variant="outline" className="ml-1 border-primary/40 text-primary text-[9px] py-0 h-4"><User className="h-2.5 w-2.5 mr-0.5" />Self</Badge></>
                  : <>Linked to +91 {customerMobile}{reusedSession && <Badge variant="outline" className="ml-2 border-success/40 text-success text-[9px] py-0 h-4">Verified · 30m</Badge>}</>}
              </p>

              {/* Customer wallet panel — balance + add funds, only in customer mode */}
              {!isSelf && customerMobile && (
                <div data-testid="customer-wallet-panel" className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Customer wallet</div>
                      <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-2xl font-bold tabular-nums" data-testid="customer-wallet-balance">
                          {walletLoading ? "…" : (customerWallet ? formatINR(customerWallet.balance) : "₹—")}
                        </span>
                        {customerWallet?.customer_name && (
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                            · {customerWallet.customer_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="cust-wallet-add-funds"
                      onClick={() => setTopupOpen(true)}
                      className="border-primary/40 text-primary hover:bg-primary/10"
                    >
                      + Add funds
                    </Button>
                  </div>
                </div>
              )}
              {cards.length === 0 ? (
                <div className="mt-6 surface-card p-6 text-center">
                  <Icon className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground mt-2">No saved {cat.label.toLowerCase()}s for this customer.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  {cards.map((c) => (
                    <div key={c.id} className={`w-full rounded-md border p-4 flex items-center gap-3 ${pickedCardId === c.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent/50"}`} data-testid={`v2-card-row-${c.id}`}>
                      <button type="button" onClick={() => setPickedCardId(c.id)}
                        data-testid={`v2-card-${c.id}`}
                        className="flex-1 text-left flex items-center gap-3">
                        <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{c.nickname}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{(c.consumer_id || "").replace(/(\d{4})/g, "$1 ").trim()} · {c.biller_name}</div>
                        </div>
                        {pickedCardId === c.id && <Badge>Selected</Badge>}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCard(c.id)}
                        disabled={deletingCardId === c.id}
                        title="Delete saved biller"
                        data-testid={`v2-card-delete-${c.id}`}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                      >
                        {deletingCardId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="outline" onClick={openAddCard} className="mt-4" data-testid="v2-add-card">
                <Plus className="h-4 w-4 mr-1" /> Add new {cat.label.toLowerCase()}
              </Button>

              {/* Show ad_field inputs for old cards that don't have stored ad_fields */}
              {pickedCardId && (() => {
                const pickedCard = cards.find((c) => c.id === pickedCardId);
                if (!pickedCard) return null;
                const biller = billers.find((b) => b.id === pickedCard.biller_id);
                const requiredAd = (biller?.ad_fields || []).filter((f) => f && f.name);
                if (requiredAd.length === 0) return null;
                const storedAd = pickedCard.ad_fields || {};
                const hasMissing = requiredAd.some((f) => !storedAd[f.key]);
                if (!hasMissing) return null;
                return (
                  <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="v2-card-ad-override">
                    <div className="text-xs font-medium text-amber-800 mb-2">Required fields for bill fetch</div>
                    <div className="space-y-2">
                      {requiredAd.map((f) => (
                        <div key={f.key}>
                          <Label className="text-xs">{f.display_name || f.name}</Label>
                          <Input
                            value={storedAd[f.key] || cardAdOverride[f.key] || ""}
                            onChange={(e) => setCardAdOverride((s) => ({ ...s, [f.key]: e.target.value }))}
                            readOnly={!!storedAd[f.key]}
                            placeholder={f.display_name || f.name}
                            className={`font-mono text-sm ${storedAd[f.key] ? "bg-muted cursor-not-allowed" : ""}`}
                            data-testid={`v2-card-ad-${f.key}`}
                          />
                          {f.regex && !storedAd[f.key] && <p className="text-[10px] text-muted-foreground mt-0.5">Format: <code>{f.regex}</code></p>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="mt-6 flex justify-end">
                <Button onClick={goToStep3} disabled={!pickedCardId || refreshing} data-testid="v2-step2-next">
                  {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  {refreshing ? "Fetching bill..." : "Continue"} {!refreshing && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </section>
          )}

          {/* Step 3 — amount + retailer charge (enhanced with min/max from BBPS + charge mode toggle) */}
          {step === 3 && (
            <section data-testid="v2-step-3">
              <h3 className="font-display text-xl font-semibold">3 · Amount + retailer charge</h3>

              {/* Customer wallet panel — same as Step 2, mirrored here so partner can
                  see balance + top-up CTA at the moment they're picking the gateway */}
              {!isSelf && customerMobile && (() => {
                const balance = customerWallet?.balance != null ? Number(customerWallet.balance) : null;
                const needsToPay = Number(collectedAmount || 0);
                const shortfall = (balance != null && needsToPay > 0) ? Math.max(0, +(needsToPay - balance).toFixed(2)) : 0;
                const insufficient = shortfall > 0 && !!customerWallet;
                return (
                  <div
                    data-testid="customer-wallet-panel-step3"
                    className={`mt-3 rounded-lg border p-3 sm:p-4 ${insufficient ? "border-destructive/40 bg-destructive/5" : "border-primary/20 bg-primary/5"}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Customer wallet</div>
                        <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
                          <span
                            className={`font-display text-2xl font-bold tabular-nums ${insufficient ? "text-destructive" : ""}`}
                            data-testid="customer-wallet-balance-step3"
                          >
                            {walletLoading ? "…" : (customerWallet ? formatINR(customerWallet.balance) : "₹—")}
                          </span>
                          {customerWallet?.customer_name && (
                            <span className="text-xs text-muted-foreground truncate max-w-[260px]">
                              · {customerWallet.customer_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => loadCustomerWallet(customerMobile)}
                          disabled={walletLoading}
                          data-testid="cust-wallet-refresh-step3"
                          title="Refresh balance"
                        >
                          <RotateCcw className={`h-3.5 w-3.5 ${walletLoading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant={insufficient ? "default" : "outline"}
                          data-testid="cust-wallet-add-funds-step3"
                          onClick={() => {
                            if (insufficient && shortfall > 0) setTopupAmount(String(shortfall));
                            setTopupOpen(true);
                          }}
                          className={insufficient ? "" : "border-primary/40 text-primary hover:bg-primary/10"}
                        >
                          + Add funds
                        </Button>
                      </div>
                    </div>
                    {insufficient && (
                      <div
                        className="mt-3 flex items-start gap-2 text-xs text-destructive"
                        data-testid="insufficient-balance-warning"
                      >
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold">Insufficient balance — </span>
                          customer needs <span className="font-mono font-semibold">{formatINR(shortfall)}</span> more to pay
                          {" "}<span className="font-mono">{formatINR(needsToPay)}</span>. Top up to continue.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Bill fetch response details */}
              {billDetails && (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-4" data-testid="v2-bill-details">
                  <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-2">Bill fetch response</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                    {billDetails.consumer_name && (
                      <div><span className="text-muted-foreground">Consumer</span><div className="font-medium mt-0.5">{billDetails.consumer_name}</div></div>
                    )}
                    {billDetails.biller_name && (
                      <div><span className="text-muted-foreground">Biller</span><div className="font-medium mt-0.5">{billDetails.biller_name}</div></div>
                    )}
                    {billDetails.bill_amount != null && (
                      <div><span className="text-muted-foreground">Bill amount</span><div className="font-mono font-semibold mt-0.5">{formatINR(billDetails.bill_amount)}</div></div>
                    )}
                    {billDetails.due_date && (
                      <div><span className="text-muted-foreground">Due date</span><div className="font-mono mt-0.5">{billDetails.due_date}</div></div>
                    )}
                    {billDetails.bill_number && (
                      <div><span className="text-muted-foreground">Bill number</span><div className="font-mono mt-0.5">{billDetails.bill_number}</div></div>
                    )}
                    {billDetails.bill_date && (
                      <div><span className="text-muted-foreground">Bill date</span><div className="font-mono mt-0.5">{billDetails.bill_date}</div></div>
                    )}
                    {billDetails.min_amount != null && (
                      <div><span className="text-muted-foreground">Min amount</span><div className="font-mono mt-0.5">{formatINR(billDetails.min_amount)}</div></div>
                    )}
                    {billDetails.max_amount != null && (
                      <div><span className="text-muted-foreground">Max amount</span><div className="font-mono mt-0.5">{formatINR(billDetails.max_amount)}</div></div>
                    )}
                    {billDetails.fetch_id && (
                      <div><span className="text-muted-foreground">Fetch ID</span><div className="font-mono mt-0.5 text-[10px] break-all">{billDetails.fetch_id}</div></div>
                    )}
                    {billDetails.fetched_at && (
                      <div><span className="text-muted-foreground">Fetched at</span><div className="font-mono mt-0.5 text-[10px]">{new Date(billDetails.fetched_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</div></div>
                    )}
                  </div>
                  {/* Additional info from BBPS */}
                  {Array.isArray(billDetails.additional_info) && billDetails.additional_info.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Additional info</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                        {billDetails.additional_info.map((info, i) => (
                          <div key={i}>
                            <span className="text-muted-foreground">{info.name || `Field ${i + 1}`}</span>
                            <div className="font-mono mt-0.5">{info.value || "—"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Min/Max range from BBPS */}
              {(() => {
                const card = cards.find((c) => c.id === pickedCardId);
                const minA = card?.last_bill_min_amount;
                const maxA = card?.last_bill_max_amount;
                if (!minA && !maxA) return null;
                return (
                  <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="v2-amount-range">
                    <div className="flex items-center gap-2 text-blue-700 font-medium text-xs">
                      <AlertCircle className="h-3.5 w-3.5" /> BBPS allowed amount range
                    </div>
                    <div className="mt-1.5 flex gap-4 text-xs">
                      {minA != null && <div>Min: <span className="font-mono font-semibold">{formatINR(minA)}</span></div>}
                      {maxA != null && <div>Max: <span className="font-mono font-semibold">{formatINR(maxA)}</span></div>}
                    </div>
                  </div>
                );
              })()}
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div>
                  <Label className="flex items-center justify-between">
                    <span>Bill amount (₹)</span>
                    <button
                      type="button"
                      onClick={refreshBill}
                      disabled={refreshing}
                      className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="v2-refresh-bill"
                    >
                      {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                      Refresh from BBPS
                    </button>
                  </Label>
                  <Input type="number" min="1" value={billAmount} onChange={(e) => setBillAmount(e.target.value)} data-testid="v2-bill-amount" className="font-mono" placeholder="0" />
                  {(() => {
                    const card = cards.find((c) => c.id === pickedCardId);
                    const minA = card?.last_bill_min_amount;
                    const maxA = card?.last_bill_max_amount;
                    const amt = parseFloat(billAmount) || 0;
                    if (minA && amt > 0 && amt < minA) {
                      return <p className="text-[11px] text-destructive mt-1">Amount below minimum ₹{minA}</p>;
                    }
                    if (maxA && amt > 0 && amt > maxA) {
                      return <p className="text-[11px] text-destructive mt-1">Amount above maximum ₹{maxA}</p>;
                    }
                    if (card?.last_bill_amount) {
                      return (
                        <p className="text-[11px] text-success mt-1" data-testid="v2-bill-prefilled">
                          Pre-filled from BBPS · {formatINR(card.last_bill_amount)}{card.last_bill_due_date ? ` · due ${card.last_bill_due_date}` : ""}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <Label>Retailer charge</Label>
                  {/* Charge mode toggle */}
                  {!isSelf && (
                    <div className="flex items-center gap-1 mt-1 mb-2">
                      <button
                        type="button"
                        onClick={() => { setRetailerChargeMode("flat"); setRetailerChargePct(""); }}
                        className={`px-3 py-1 text-xs font-medium rounded-l-md border ${retailerChargeMode === "flat" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
                        data-testid="v2-charge-mode-flat"
                      >
                        ₹ Flat
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRetailerChargeMode("pct"); setRetailerCharge(""); }}
                        className={`px-3 py-1 text-xs font-medium rounded-r-md border ${retailerChargeMode === "pct" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
                        data-testid="v2-charge-mode-pct"
                      >
                        % Percentage
                      </button>
                    </div>
                  )}
                  {retailerChargeMode === "flat" ? (
                    <Input
                      type="number" min="0"
                      value={isSelf ? "0" : retailerCharge}
                      onChange={(e) => setRetailerCharge(e.target.value)}
                      disabled={isSelf}
                      data-testid="v2-retailer-charge"
                      className="font-mono"
                      placeholder="0"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number" min="0" max="100" step="0.1"
                        value={isSelf ? "0" : retailerChargePct}
                        onChange={(e) => setRetailerChargePct(e.target.value)}
                        disabled={isSelf}
                        data-testid="v2-retailer-charge-pct"
                        className="font-mono"
                        placeholder="0"
                      />
                      <span className="text-sm text-muted-foreground font-medium">%</span>
                    </div>
                  )}
                  {/* Show computed charge for percentage mode */}
                  {!isSelf && retailerChargeMode === "pct" && effectiveRetailerCharge > 0 && (
                    <p className="text-[11px] text-primary mt-1" data-testid="v2-charge-computed">
                      = {formatINR(effectiveRetailerCharge)}
                    </p>
                  )}
                  {/* Show admin caps */}
                  {!isSelf && (retailerChargeCaps.max_flat > 0 || retailerChargeCaps.max_pct > 0) && (
                    <p className="text-[11px] text-muted-foreground mt-1" data-testid="v2-charge-caps">
                      Admin cap:
                      {retailerChargeCaps.max_flat > 0 && <span className="ml-1">max {formatINR(retailerChargeCaps.max_flat)} flat</span>}
                      {retailerChargeCaps.max_flat > 0 && retailerChargeCaps.max_pct > 0 && <span className="mx-1">or</span>}
                      {retailerChargeCaps.max_pct > 0 && <span className="ml-1">{retailerChargeCaps.max_pct}% of bill</span>}
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">{isSelf ? "Not applicable in self-mode (you're paying your own bill)." : "Credited to your wallet on L3 success."}</p>
                </div>
              </div>
              <div className="mt-5 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
                <span className="text-muted-foreground">{isSelf ? "You'll pay from your wallet:" : "Customer pays:"}</span> <span className="font-mono font-bold ml-1">{formatINR(collectedAmount)}</span>
                <div className="text-[11px] text-muted-foreground mt-1">
                  bill {formatINR(parseFloat(billAmount) || 0)}
                  {!isSelf && effectiveRetailerCharge > 0 && <> + charge {formatINR(effectiveRetailerCharge)}</>}
                  {platformFee > 0 && <> + platform fee {formatINR(platformFee)}</>}
                  {previewLoading && <span className="ml-1 text-muted-foreground">…</span>}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={() => setStep(4)} disabled={!billAmount || parseFloat(billAmount) <= 0} data-testid="v2-step3-next">Continue <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </section>
          )}

          {/* Step 4 — gateway + method */}
          {step === 4 && (
            <section data-testid="v2-step-4">
              {isSelf ? (
                <>
                  <h3 className="font-display text-xl font-semibold">4 · Confirm payment</h3>
                  <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Wallet className="h-4 w-4 text-primary" />
                      Pay from your partner wallet
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Self-mode · No PG checkout · No customer KYC needed. Bill goes through L1 → L2 → L3 approval before settlement.
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                      <div><span className="text-muted-foreground">Bill amount</span><div className="font-mono font-semibold mt-0.5">{formatINR(parseFloat(billAmount) || 0)}</div></div>
                      <div><span className="text-muted-foreground">Retailer charge</span><div className="font-mono mt-0.5">₹0 <span className="text-muted-foreground">(self)</span></div></div>
                      <div><span className="text-muted-foreground">Platform fee</span><div className="font-mono mt-0.5">{platformFee > 0 ? formatINR(platformFee) : (previewLoading ? "…" : "—")}</div></div>
                      <div><span className="text-muted-foreground">You'll pay</span><div className="font-mono font-bold mt-0.5">{formatINR(collectedAmount)}</div></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-display text-xl font-semibold">4 · Confirm payment</h3>
                  <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Wallet className="h-4 w-4 text-primary" />
                      Debit from customer wallet
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Total <span className="font-mono font-semibold text-foreground">{formatINR(collectedAmount)}</span> (bill {formatINR(parseFloat(billAmount) || 0)}{effectiveRetailerCharge > 0 ? ` + charge ${formatINR(effectiveRetailerCharge)}` : ""}{platformFee > 0 ? ` + platform fee ${formatINR(platformFee)}` : ""}) will be debited from the customer's wallet. Transaction goes through L1 → L2 → L3 approval before BBPS settlement.
                    </div>
                  </div>
                </>
              )}
              <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2">
                <Button variant="outline" onClick={() => setStep(3)} className="sm:order-none order-2">Back</Button>
                <Button onClick={submit} disabled={submitting} data-testid="v2-submit" className="sm:order-none order-1">
                  {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                  {/* Compact wording on mobile so the button doesn't wrap awkwardly. */}
                  <span className="sm:hidden">
                    {isSelf ? `Pay ${formatINR(collectedAmount)}` : `Collect ${formatINR(collectedAmount)}`}
                  </span>
                  <span className="hidden sm:inline">
                    {isSelf ? `Pay ${formatINR(collectedAmount)} from MY wallet` : `Submit · Customer pays ${formatINR(collectedAmount)}`}
                  </span>
                </Button>
              </div>
            </section>
          )}

          {/* Result */}
          {step === 6 && result && (
            <section data-testid="v2-result">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-success" />
                <h3 className="font-display text-xl font-semibold">Submission successful</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {result.transaction.display_id && (
                  <>Txn ID: <CopyableId value={result.transaction.display_id} className="mr-2" /></>
                )}
                Status: <code className="font-mono text-foreground">{result.transaction.status}</code> · {result.transaction.requires_approval ? "L1 → L2 → L3 approval" : "Direct settlement"}.
              </p>
              {result.payment_link_url && (
                <div className="mt-4 rounded-md border border-success/40 bg-success/5 p-3 text-sm">
                  <div className="font-medium">Share this link with the customer</div>
                  <div className="font-mono text-xs break-all mt-1">{result.payment_link_url}</div>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard?.writeText(result.payment_link_url); toast.success("Copied"); }}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
              )}
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                <Metric label="Bill amount" value={formatINR(result.transaction.amount)} />
                <Metric label="Retailer charge" value={formatINR(result.transaction.retailer_charge)} />
                <Metric label="Customer pays" value={formatINR(result.transaction.collected_amount)} />
                <Metric label="PG fee" value={formatINR(result.fee)} />
              </div>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={() => navigate("/transactions")}>View transactions</Button>
                <Button onClick={() => { setResult(null); setBillAmount(""); setBillDetails(null); setRetailerCharge(""); setRetailerChargePct(""); setRetailerChargeMode("flat"); setPickedCardId(""); if (isSelf) { setStep(2); } else { setStep(1); setOtpSent(false); setOtp(""); } }}>New bill</Button>
              </div>
            </section>
          )}
        </div>

        {/* Right summary */}
        <aside className="surface-card p-5 h-fit sticky top-4">
          <h4 className="font-display font-semibold">Summary</h4>
          <div className="mt-3 space-y-2 text-xs">
            <Row label="Mode" value={isSelf ? <Badge className="text-[9px] py-0 h-4"><User className="h-2.5 w-2.5 mr-0.5" />Self</Badge> : <Badge variant="outline" className="text-[9px] py-0 h-4">Customer-pays</Badge>} />
            <Row label="Category" value={cat.label} />
            <Row label={isSelf ? "Partner" : "Customer"} value={customerMobile ? <>+91 {customerMobile}{!isSelf && reusedSession && <Badge variant="outline" className="ml-1 border-success/50 text-success text-[9px] py-0 h-4">30m</Badge>}</> : <em>—</em>} />
            <Row label="Card" value={cards.find((c) => c.id === pickedCardId)?.nickname || <em>—</em>} />
            <Row label="Bill" value={billAmount ? formatINR(parseFloat(billAmount)) : <em>—</em>} />
            <Row label="Retailer charge" value={isSelf ? <em>n/a (self)</em> : (effectiveRetailerCharge > 0 ? <span className="font-mono">{formatINR(effectiveRetailerCharge)}{retailerChargeMode === "pct" ? <span className="text-muted-foreground ml-1">({retailerChargePct}%)</span> : ""}</span> : <em>—</em>)} />
            <Row
              label={<span title="Subscription slabs + percent + OTP — configured by admin">Platform fee</span>}
              value={platformFee > 0
                ? <span className="font-mono">{formatINR(platformFee)}</span>
                : (previewLoading ? <em className="text-muted-foreground">…</em> : <em>—</em>)}
            />
            <Row label={isSelf ? "You'll pay" : "Customer pays"} value={collectedAmount ? <strong>{formatINR(collectedAmount)}</strong> : <em>—</em>} />
            <Row label="Method" value={isSelf ? "partner_wallet" : paymentMethod} />
            <Row label="Gateway" value={isSelf ? "partner_wallet" : "customer_wallet"} />
          </div>
          <div className="mt-4 rounded-md bg-warning/5 border border-warning/30 p-3 text-[11px]">
            <AlertCircle className="h-3 w-3 inline mr-1 text-warning" />
            All {cat.label.toLowerCase()} bills require <strong>L1 → L2 → L3</strong> approval before settlement.
          </div>
        </aside>
      </div>

      {/* Add card dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isSelf ? `Add MY ${cat.label.toLowerCase()}` : `Add ${cat.label.toLowerCase()} for +91 ${customerMobile}`}</DialogTitle>
            <DialogDescription>{isSelf ? "BBPS-validated · linked to your partner account." : "BBPS-validated · OTP verified · linked to this customer."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-primary/20 bg-primary/5 p-2.5 text-[11px] flex items-start gap-2" data-testid="v2-newcard-mobile-hint">
              <ShieldCheck className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <div>
                <span className="text-muted-foreground">Mobile auto-attached:</span>
                <span className="font-mono ml-1 font-medium">+91 {isSelf ? (user?.mobile || "—") : (customerMobile || "—")}</span>
                <div className="text-muted-foreground mt-0.5">Sent to BBPS validation as the registered mobile for this {cat.label.toLowerCase()}.</div>
              </div>
            </div>
            <div>
              <Label>Biller (BBPS-registered)</Label>
              <BillerSearch
                billers={billers}
                loading={billersLoading}
                value={newCard.biller_id}
                onSelect={(id) => {
                  const sel = billers.find((b) => b.id === id);
                  const dn = (sel?.displayname || "").toLowerCase();
                  // Auto-fill consumer_id with customer mobile if biller asks for a mobile number
                  const autoFillMobile = (dn.includes("mobile") || dn.includes("phone"))
                    ? (isSelf ? (user?.mobile || "") : customerMobile)
                    : "";
                  setNewCard((c) => ({ ...c, biller_id: id, consumer_name: "", consumer_id: autoFillMobile || c.consumer_id }));
                  setNewCardAdFields({});
                  setValidated(false); setValidatedAmount(null);
                }}
              />
              {!billersLoading && billers.length === 0 ? (
                <p className="text-[11px] text-warning mt-1" data-testid="v2-newcard-no-billers">
                  BBPS provider returned no billers. They may be in maintenance — try again in a few minutes,
                  or ask admin to run "Refresh catalog" in Integrations.
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-1">Live list from your BBPS provider · cached fallback if provider is down.</p>
              )}
            </div>
            <div>
              <Label>{(() => {
                const selBiller = billers.find((b) => b.id === newCard.biller_id);
                return selBiller?.displayname || cat.inputLabel;
              })()}</Label>
              {(() => {
                const selBiller = billers.find((b) => b.id === newCard.biller_id);
                const dn = (selBiller?.displayname || "").toLowerCase();
                const isMobileField = dn.includes("mobile") || dn.includes("phone");
                const isAutoFilled = isMobileField && newCard.consumer_id;
                return (
                  <>
                    <div className="flex gap-2">
                      <Input
                        value={newCard.consumer_id}
                        onChange={(e) => {
                          if (isAutoFilled) return;
                          setNewCard((c) => ({ ...c, consumer_id: e.target.value, consumer_name: "" })); setValidated(false);
                        }}
                        readOnly={!!isAutoFilled}
                        placeholder={selBiller?.displayname || cat.inputHint}
                        className={`font-mono ${isAutoFilled ? "bg-muted cursor-not-allowed" : ""}`}
                        data-testid="v2-newcard-consumerid"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={validateConsumer}
                        disabled={validating || !newCard.biller_id || !newCard.consumer_id}
                        data-testid="v2-newcard-validate"
                      >
                        {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                        <span className="ml-1">Validate</span>
                      </Button>
                    </div>
                    {isAutoFilled && (
                      <p className="text-[10px] text-muted-foreground mt-1">Auto-filled from customer mobile</p>
                    )}
                  </>
                );
              })()}
              {!validated && !validateError && newCard.consumer_id && (
                <p className="text-[11px] text-warning mt-1">Tap Validate to fetch bill details from BBPS.</p>
              )}
              {(() => {
                const selBiller = billers.find((b) => b.id === newCard.biller_id);
                if (selBiller?.regex) {
                  return <p className="text-[11px] text-muted-foreground mt-1">Format: <code className="font-mono">{selBiller.regex}</code></p>;
                }
                return null;
              })()}
            </div>
            {/* Dynamic ad_fields from biller metadata (e.g. Circle for mobile-prepaid) */}
            {(() => {
              const selBiller = billers.find((b) => b.id === newCard.biller_id);
              const ad = (selBiller?.ad_fields || []).filter((f) => f && f.name);
              if (ad.length === 0) return null;
              const custMobile = isSelf ? (user?.mobile || "") : customerMobile;
              return (
                <div className="space-y-2" data-testid="v2-newcard-ad-fields">
                  {ad.map((f) => {
                    const fnLower = (f.display_name || f.name || "").toLowerCase();
                    const isMobileField = fnLower.includes("mobile") || fnLower.includes("phone");
                    const autoVal = isMobileField && custMobile && !newCardAdFields[f.key] ? custMobile : "";
                    const displayVal = newCardAdFields[f.key] || autoVal;
                    return (
                      <div key={f.key}>
                        <Label className="text-xs">{f.display_name || f.name}</Label>
                        <Input
                          value={displayVal}
                          onChange={(e) => {
                            if (isMobileField && autoVal) return;
                            setNewCardAdFields((s) => ({ ...s, [f.key]: e.target.value }));
                            setValidated(false);
                          }}
                          readOnly={!!(isMobileField && autoVal)}
                          placeholder={f.display_name || f.name}
                          className={`font-mono text-sm ${isMobileField && autoVal ? "bg-muted cursor-not-allowed" : ""}`}
                          data-testid={`v2-newcard-${f.key}`}
                        />
                        {isMobileField && autoVal && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">Auto-filled from customer mobile</p>
                        )}
                        {f.regex && !isMobileField && (
                          <p className="text-[11px] text-muted-foreground mt-0.5">Format: <code className="font-mono">{f.regex}</code></p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Validation result — success */}
            {validated && (
              <div className="rounded-md border border-success/40 bg-success/5 p-3 text-sm" data-testid="v2-newcard-validated">
                <div className="flex items-center gap-2 text-success font-medium text-xs">
                  <ShieldCheck className="h-3.5 w-3.5" /> BBPS validated
                </div>
                {newCard.consumer_name && (
                  <div className="mt-1.5">
                    <span className="text-muted-foreground text-xs">Cardholder:</span> <span className="font-medium">{newCard.consumer_name}</span>
                  </div>
                )}
                {validatedAmount != null && validatedAmount > 0 && (
                  <div className="mt-0.5 text-xs text-muted-foreground">Current outstanding: <span className="font-mono text-foreground">{formatINR(validatedAmount)}</span></div>
                )}
                {validatedDue && (
                  <div className="mt-0.5 text-xs text-muted-foreground">Due: <span className="font-mono text-foreground">{validatedDue}</span></div>
                )}
                {!newCard.consumer_name && (
                  <div className="mt-2 text-xs text-warning">No cardholder name returned — please type it manually below.</div>
                )}
              </div>
            )}
            {/* Validation result — error (with retry / save-anyway fallback) */}
            {validateError && !validated && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="v2-newcard-validate-error">
                <div className="flex items-center gap-2 text-destructive font-medium text-xs">
                  <AlertCircle className="h-3.5 w-3.5" /> BBPS could not validate
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">{validateError}</div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  You can still save this {cat.label.toLowerCase()} manually — fill in the name below.
                  When you initiate the actual bill payment, BBPS will re-validate end-to-end.
                </div>
              </div>
            )}
            <div>
              <Label>Cardholder / Customer name {validated ? "" : <span className="text-muted-foreground text-[10px]">(manual)</span>}</Label>
              <Input
                value={newCard.consumer_name}
                onChange={(e) => setNewCard({ ...newCard, consumer_name: e.target.value })}
                placeholder="As printed on card / loan agreement"
                data-testid="v2-newcard-name"
              />
            </div>
            <div>
              <Label>Nickname</Label>
              <Input
                value={newCard.nickname}
                onChange={(e) => setNewCard({ ...newCard, nickname: e.target.value })}
                placeholder={category === "credit_card" ? "HDFC Diners" : "Personal loan"}
                data-testid="v2-newcard-nickname"
              />
              <p className="text-[11px] text-muted-foreground mt-1">A short name for this {cat.label.toLowerCase()} — only the customer & retailer see it.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addNewCard} disabled={busy || !validated || !newCard.nickname || !newCard.consumer_name} data-testid="v2-newcard-save">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer wallet top-up dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent data-testid="cust-wallet-topup-dialog">
          <DialogHeader>
            <DialogTitle>Add funds to customer wallet</DialogTitle>
            <DialogDescription>
              +91 {customerMobile} · current balance{" "}
              <span className="font-mono">{customerWallet ? formatINR(customerWallet.balance) : "—"}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cust-topup-amount" className="text-xs">Amount (₹)</Label>
              <Input
                id="cust-topup-amount"
                data-testid="cust-topup-amount"
                type="number"
                min={1}
                step={1}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="font-mono mt-1"
                placeholder="500"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[500, 1000, 2000, 5000].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`cust-topup-preset-${v}`}
                    onClick={() => setTopupAmount(String(v))}
                    className="text-[11px] px-2 py-1 rounded-full border border-border hover:bg-accent font-mono"
                  >
                    +₹{v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="cust-topup-gateway" className="text-xs">
                Gateway {!topupOptionsLoaded && <span className="text-muted-foreground">· loading…</span>}
              </Label>
              <select
                id="cust-topup-gateway"
                data-testid="cust-topup-gateway"
                value={topupGateway}
                onChange={(e) => { setTopupGateway(e.target.value); setTopupChannel(""); }}
                disabled={!topupOptionsLoaded || availableGateways.length === 0}
                className="mt-1 w-full font-mono text-sm rounded-md border border-input bg-background px-2 py-1.5 disabled:opacity-60"
              >
                {availableGateways.length === 0 && topupOptionsLoaded && (
                  <option value="">No gateways configured — contact admin</option>
                )}
                {availableGateways.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
              {topupOptionsLoaded && availableGateways.length === 0 && (
                <p className="mt-1 text-[11px] text-destructive">
                  No PG has both credentials configured AND a top-up fee rule. Ask admin to enable a gateway in <code className="font-mono">/admin/topup-fees</code>.
                </p>
              )}
            </div>
            {topupGateway === "manarecharge" && (() => {
              const meta = (topupOptions.gateways || []).find((g) => g.id === "manarecharge");
              if (!meta?.supports_channel) return null;
              return (
              <div>
                <Label htmlFor="cust-topup-channel" className="text-xs">
                  Channel <span className="text-muted-foreground">(optional — uses gateway default if blank)</span>
                </Label>
                <select
                  id="cust-topup-channel"
                  data-testid="cust-topup-channel"
                  value={topupChannel}
                  onChange={(e) => setTopupChannel(e.target.value)}
                  className="mt-1 w-full font-mono text-sm rounded-md border border-input bg-background px-2 py-1.5"
                  disabled={manaChannelsLoading}
                >
                  <option value="">Default (admin-configured)</option>
                  {manaChannels.map((ch) => (
                    <option key={ch.id} value={ch.id} disabled={ch.active === false}>
                      {ch.name || ch.id}{ch.active === false ? " (inactive)" : ""}
                    </option>
                  ))}
                </select>
                {!manaChannelsLoading && manaChannels.length === 0 && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Live channel list unavailable — will use admin default.
                  </p>
                )}
              </div>
              );
            })()}

            {/* Live fee preview — what the customer pays at the PG and what's
                credited to the wallet, computed from admin's master + package
                adjustment. Mirrors `/topup-fees/preview` response shape. */}
            {parseFloat(topupAmount) > 0 && (
              <div className="rounded-md border border-border bg-muted/20 p-2.5 text-[11px] space-y-1" data-testid="cust-topup-fee-preview">
                <div className="font-semibold flex items-center justify-between">
                  <span>Fee preview</span>
                  {topupFeeLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </div>
                {topupFeeError ? (
                  <div className="text-destructive" data-testid="cust-topup-fee-error">{topupFeeError}</div>
                ) : topupFeePreview ? (
                  <div className="font-mono">
                    <div className="flex justify-between"><span>Top-up amount</span><span>₹{parseFloat(topupAmount).toFixed(2)}</span></div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ PG MDR fee</span>
                      <span data-testid="cust-topup-fee-mdr">₹{Number(topupFeePreview.mdr_fee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t border-border mt-1 pt-1">
                      <span>Customer pays at PG</span>
                      <span data-testid="cust-topup-fee-gross">₹{Number(topupFeePreview.gross_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground mt-1">
                      <span>− Settlement fee</span>
                      <span data-testid="cust-topup-fee-settle">₹{Number(topupFeePreview.settle_fee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-success border-t border-border mt-1 pt-1">
                      <span>Credited to wallet</span>
                      <span data-testid="cust-topup-fee-credited">₹{Number(topupFeePreview.credited_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-[9px] text-muted-foreground mt-1 italic">
                      Fee source: {topupFeePreview.fee_source || "master"}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">Enter amount + pick gateway to see fees…</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)} disabled={topupSubmitting}>Cancel</Button>
            <Button
              onClick={submitCustomerTopup}
              disabled={topupSubmitting || !parseFloat(topupAmount)}
              data-testid="cust-topup-submit"
            >
              {topupSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Create top-up order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border/50 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span>{value}</span>
  </div>
);

const Metric = ({ label, value }) => (
  <div className="rounded-md border border-border p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono mt-1">{value}</div>
  </div>
);
